// src/components/SectionShimmer.jsx
// Section-wise skeleton shimmers for City / Street / PND detail panels.
// Drop-in replacements for the old <Loader2 className="animate-spin" /> loading rows.

import React from 'react';

const Bar = ({ w = 'w-full', h = 'h-3', className = '' }) => (
  <div className={`${w} ${h} rounded bg-muted-foreground/15 animate-pulse ${className}`} />
);

// ============================================
// 1) StatsGridShimmer
// Used for: Overview cards, Device Type Breakdown, Revenue Overview (2-col grids)
// ============================================
export const StatsGridShimmer = ({ count = 4 }) => (
  <div className="grid grid-cols-2 gap-2.5">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-xl border border-border bg-secondary p-3">
        <Bar w="w-16" h="h-2" className="mb-2 opacity-70" />
        <Bar w="w-12" h="h-6" />
      </div>
    ))}
  </div>
);

// ============================================
// 2) PaymentTableShimmer
// Used for: PaymentBreakdownCard (City / Street revenue section)
// Mimics header + 2 group rows + a few child rows + grand total
// ============================================
export const PaymentTableShimmer = ({ groups = 2, rowsPerGroup = 3 }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden w-full">
    {/* header */}
    <div className="border-b border-border">
      <div className="flex items-center gap-2 px-3.5 pt-2.5 pb-1.5">
        <Bar w="w-3.5" h="h-3.5" className="rounded-full" />
        <Bar w="w-32" h="h-3" />
      </div>
      <div className="grid grid-cols-6 gap-1 px-3.5 py-1.5 bg-zinc-100 dark:bg-zinc-800">
        {Array.from({ length: 6 }).map((_, i) => (
          <Bar key={i} w="w-8" h="h-2" className="opacity-60" />
        ))}
      </div>
    </div>

    {Array.from({ length: groups }).map((_, gi) => (
      <div key={gi} className="border-b border-border last:border-b-0">
        {/* group header row */}
        <div className="grid grid-cols-6 gap-1 items-center px-3.5 py-2.5 bg-[#ddebf7]/40">
          {Array.from({ length: 6 }).map((_, i) => (
            <Bar key={i} w={i === 0 ? 'w-14' : 'w-8'} h="h-3" className="opacity-70" />
          ))}
        </div>
        {/* child rows */}
        {Array.from({ length: rowsPerGroup }).map((_, ri) => (
          <div key={ri} className="grid grid-cols-6 gap-1 items-center px-3.5 py-2 border-b border-border/50">
            {Array.from({ length: 6 }).map((_, ci) => (
              <Bar key={ci} w={ci === 0 ? 'w-12' : 'w-6'} h="h-2.5" className="opacity-50" />
            ))}
          </div>
        ))}
      </div>
    ))}

    {/* grand total */}
    <div className="flex items-center gap-2 px-3.5 py-2.5 bg-zinc-200/60 dark:bg-zinc-700/40">
      <Bar w="w-20" h="h-3" />
      <div className="flex-1" />
      <Bar w="w-10" h="h-3" />
    </div>
  </div>
);

// ============================================
// 3) PndListShimmer
// Used for: PND breakdown table (StreetRevenueSection) & Streets Revenue list
// (CityStreetsRevenueSummary rows)
// ============================================
export const PndListShimmer = ({ rows = 5, withHeader = true }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    {withHeader && (
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-border">
        <Bar w="w-3.5" h="h-3.5" className="rounded-full" />
        <Bar w="w-24" h="h-3" />
        <div className="flex-1" />
        <Bar w="w-14" h="h-4" className="rounded-full" />
      </div>
    )}
    <div className="divide-y divide-border">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3.5 py-2.5">
          <Bar w="w-2" h="h-2" className="rounded-full flex-shrink-0" />
          <Bar w="w-16" h="h-3" />
          <div className="flex-1" />
          <Bar w="w-10" h="h-3" />
          <Bar w="w-10" h="h-3" />
          <Bar w="w-12" h="h-4" className="rounded-full" />
        </div>
      ))}
    </div>
  </div>
);

// ============================================
// 4) ChartShimmer
// Generic light/dark theme bar-chart skeleton (uses theme CSS vars).
// NOTE: Don't use this inside hardcoded-dark components like AreaChart —
// use AreaChartShimmer below for those instead.
// ============================================
export const ChartShimmer = ({ bars = 12 }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <Bar w="w-32" h="h-2.5" className="mb-4 opacity-70" />
    <div className="flex items-end gap-1.5 h-28">
      {Array.from({ length: bars }).map((_, i) => {
        const heightPct = 30 + ((i * 37) % 65); // deterministic pseudo-random heights
        return (
          <div
            key={i}
            className="flex-1 rounded-t bg-muted-foreground/15 animate-pulse"
            style={{ height: `${heightPct}%` }}
          />
        );
      })}
    </div>
    <div className="flex justify-between mt-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Bar key={i} w="w-6" h="h-2" className="opacity-50" />
      ))}
    </div>
  </div>
);

// ============================================
// 5) SectionShimmerWrap
// Small helper: keeps existing spacing consistent when swapping loading UI
// ============================================
export const SectionShimmerWrap = ({ children }) => (
  <div className="py-1">{children}</div>
);

// ============================================
// 6) AreaChartShimmer
// Exact dark-theme match for AreaChart.jsx (bg #464647).
// Hardcoded dark colors — does NOT use theme CSS vars — so it never
// flashes white/light regardless of the app's light/dark mode.
// Used in HourlyRevenueChart while data loads.
// ============================================
export const AreaChartShimmer = ({ columns = 24, height = 260 }) => (
  <div className="w-full p-3" style={{ backgroundColor: '#464647' }}>

    {/* header row placeholder (badge / title) */}
    <div className="flex items-center justify-between mb-2" style={{ minHeight: '2rem' }}>
      <div
        className="rounded animate-pulse"
        style={{ width: '90px', height: '20px', backgroundColor: 'rgba(255,255,255,0.12)' }}
      />
      <div
        className="rounded animate-pulse"
        style={{ width: '140px', height: '14px', backgroundColor: 'rgba(255,255,255,0.08)' }}
      />
    </div>

    {/* chart area — gradient pulse mimicking the area chart */}
    <div
      className="relative w-full rounded overflow-hidden animate-pulse"
      style={{
        height: `${height}px`,
        background: 'linear-gradient(180deg, rgba(16,185,129,0.28) 0%, rgba(16,185,129,0.06) 100%)',
      }}
    >
      {/* wavy baseline hint */}
      <svg
        viewBox="0 0 400 100"
        preserveAspectRatio="none"
        style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '55%', opacity: 0.35 }}
      >
        <path
          d="M0,70 C40,40 80,85 120,55 C160,25 200,75 240,45 C280,15 320,60 360,35 C380,25 390,40 400,30 L400,100 L0,100 Z"
          fill="rgba(16,185,129,0.5)"
        />
      </svg>
    </div>

    {/* table rows — time header + value row */}
    <div className="w-full mt-1">
      <div className="flex" style={{ border: '1px solid rgba(255,255,255,0.15)', borderBottom: 'none' }}>
        <div style={{ width: '8%', padding: '4px 2px', borderRight: '1px solid rgba(255,255,255,0.15)' }} />
        {Array.from({ length: columns }).map((_, i) => (
          <div
            key={i}
            className="animate-pulse"
            style={{
              width: `${92 / columns}%`,
              padding: '4px 0',
              borderRight: '1px solid rgba(255,255,255,0.15)',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <div style={{ width: '10px', height: '6px', borderRadius: '2px', backgroundColor: 'rgba(255,255,255,0.15)' }} />
          </div>
        ))}
      </div>
      <div className="flex" style={{ border: '1px solid rgba(255,255,255,0.15)' }}>
        <div style={{ width: '8%', padding: '5px 2px', borderRight: '1px solid rgba(255,255,255,0.15)' }} />
        {Array.from({ length: columns }).map((_, i) => (
          <div
            key={i}
            className="animate-pulse"
            style={{
              width: `${92 / columns}%`,
              padding: '5px 0',
              backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.04)',
              borderRight: '1px solid rgba(255,255,255,0.15)',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <div style={{ width: '14px', height: '6px', borderRadius: '2px', backgroundColor: 'rgba(255,255,255,0.15)' }} />
          </div>
        ))}
      </div>
    </div>

    <div className="text-center font-semibold mt-2" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>
      TIME
    </div>
  </div>
);